import React, { useEffect, useState } from 'react';
import moment from 'moment';

import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { toggleDailyReport, toggleSftpReport, updateReportScheduling } from 'redux/actions/custom-downloads';

import NvDropdown, {
  NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import { DataDownloadInfo } from 'redux/schemas/api/custom-downloads';
import DailySftpSwitch from './daily-sftp-switch';
import DailyEmailSwitch from './daily-email-switch';
import { DailyReportConfig } from './daily-report-configuration-modal';

type DownloadsCardOptionsProps = {
  downloadInfo: DataDownloadInfo
  type: 'dataDownload' | 'contentDownload'
  catalogId: string
  showSftpToggle: boolean
  editReport?: () => void
  duplicateReport?: () => void
  deleteReport?: () => void
  // Notifies the owner that this report's fields changed so it can patch its own store.
  onReportUpdated?: (reportId: number, patch: Partial<DataDownloadInfo>) => void
};

const DownloadsCardOptions: React.FC<DownloadsCardOptionsProps> = ({
  downloadInfo,
  type,
  catalogId,
  showSftpToggle,
  editReport,
  duplicateReport,
  deleteReport,
  onReportUpdated,
}) => {
  const {
    reportId,
    isDailySftp,
    isSentDaily,
    scheduledAt,
    recipientUserIds,
    isDuplicatePlaceholder,
  } = downloadInfo;

  const [hasDailySftpEnabled, setHasDailySftpEnabled] = useState(isDailySftp);
  const [hasDailyReprotEnabled, setHasDailyReportEnabled] = useState(isSentDaily);
  const [isTogglingSftp, setIsTogglingSftp] = useState(false);
  const [isTogglingReport, setIsTogglingReport] = useState(false);
  const [initialConfig, setInitConfig] = useState({
    scheduledAt: scheduledAt || '',
    recipientUserIds: recipientUserIds || [],
  });

  const dispatch = useAppDispatch();

  // Keep the dropdown in sync when the owner refreshes downloadInfo (e.g. a
  // Pusher-triggered reload), otherwise the toggles show stale server values.
  useEffect(() => {
    setHasDailySftpEnabled(isDailySftp);
    setHasDailyReportEnabled(isSentDaily);
    setInitConfig({
      scheduledAt: scheduledAt || '',
      recipientUserIds: recipientUserIds || [],
    });
  }, [isDailySftp, isSentDaily, scheduledAt, recipientUserIds]);

  const onToggleSftpReport = () => {
    setIsTogglingSftp(true);
    dispatch(toggleSftpReport({ catalogId, reportId }))
      .then((result) => {
        if (!result.error) {
          const enabled = !hasDailySftpEnabled;
          setHasDailySftpEnabled(enabled);
          onReportUpdated?.(reportId, { isDailySftp: enabled });
        }
        setIsTogglingSftp(false);
      });
  };

  const onToggleDailyReport = () => {
    setIsTogglingReport(true);
    dispatch(toggleDailyReport({ catalogId, reportId }))
      .then((result) => {
        if (!result.error) {
          const enabled = !hasDailyReprotEnabled;
          setHasDailyReportEnabled(enabled);
          onReportUpdated?.(reportId, { isSentDaily: enabled });
        }
        setIsTogglingReport(false);
      });
  };

  const handleReportConfig = async (data: DailyReportConfig) => {
    const courseCustomDataReport = {
      recipientUserIds: (data.recipientUserIds || []).map(admin => admin.user.id),
      // The selected time is in user's local timezone, need to convert to UTC time for backend
      scheduledAt: moment(data.scheduledAt).utc().format('HH:mm'),
    };
    await dispatch(updateReportScheduling({
      courseCustomDataReport,
      catalogId,
      reportId,
    })).then((result) => {
      if (result.payload) {
        setInitConfig(result.payload);
        onReportUpdated?.(reportId, result.payload);
      }
    });
    return true;
  };

  // A duplicating placeholder has no real reportId yet,
  // hide the menu until duplication finishes.
  if (isDuplicatePlaceholder) return null;

  let options: NvDropdownOption[];
  const deleteOption: NvDropdownOption | null = deleteReport ? {
    type: 'text',
    text: t.FORM.DELETE(),
    class: 'text-danger',
    callback: deleteReport,
  } : null;
  if (type === 'dataDownload') {
    const dataDownloadoptions: NvDropdownOption[] = [];

    if (editReport) {
      dataDownloadoptions.push({
        type: 'custom',
        customItem: (
          <ClickableContainer
            className={`d-flex gray-1 px-4 py-1 align-items-center ${hasDailySftpEnabled ? 'gray-4' : ''}`}
            onClick={editReport}
            disabled={hasDailySftpEnabled}
          >
            {t.FORM.EDIT()}
            {hasDailySftpEnabled && (
              <NvTooltip text={t.CUSTOM_DOWNLOADS.SFTP_INFO()}>
                <NvIcon
                  icon='info'
                  size='xs-smallest'
                  className=' ml-1 text-primary'
                />
              </NvTooltip>
            )}
          </ClickableContainer>),
        disabled: hasDailySftpEnabled,
      });
    }

    if (duplicateReport) {
      dataDownloadoptions.push({
        type: 'text',
        text: t.CUSTOM_DOWNLOADS.DUPLICATE(),
        callback: duplicateReport,
        class: 'text-gray-1',
      });
    }

    if (dataDownloadoptions.length) {
      dataDownloadoptions.push({
        type: 'divider',
      });
    }

    if (showSftpToggle) {
      dataDownloadoptions.push(
        isTogglingSftp ? {
          type: 'text',
          text: hasDailySftpEnabled ? t.CUSTOM_DOWNLOADS.DISABLING() : t.CUSTOM_DOWNLOADS.ENABLING(),
          disabled: true,
        } : {
          type: 'custom',
          customItem: (
            <DailySftpSwitch
              isDailysftp={hasDailySftpEnabled}
              toggleSftpReport={onToggleSftpReport}
            />
          ),
        },
        {
          type: 'divider',
        },
      );
    }

    dataDownloadoptions.push(
      isTogglingReport ? {
        type: 'text',
        text: hasDailyReprotEnabled ? t.CUSTOM_DOWNLOADS.DISABLING() : t.CUSTOM_DOWNLOADS.ENABLING(),
        disabled: true,
      } : {
        type: 'custom',
        customItem: (
          <DailyEmailSwitch
            isDailyReportEnabled={hasDailyReprotEnabled}
            toggleDailyReport={onToggleDailyReport}
            onUpdateReportConfiguration={handleReportConfig}
            initialConfig={initialConfig}
          />
        ),
      },
      {
        type: 'divider',
      },
    );

    if (deleteOption) dataDownloadoptions.push(deleteOption);
    options = dataDownloadoptions;
  } else {
    options = deleteOption ? [deleteOption] : [];
  }

  return (
    <NvDropdown
      items={options}
      buttonStyle={NvDropdownButtonStyle.ICON}
      iconClass='icon-smallest icon-settings gray-3'
      align={NvDropdownAlign.RIGHT}
      minWidth={type === 'dataDownload' ? 250 : null}
    />
  );
};

export default DownloadsCardOptions;
